'use strict';

var _ = require('lodash');
var GridSettingsMergeService = function GridSettingsMergeService($log, GridSettings, GridColumn, GridColumnGroup) {
  /**
   * Checks if given object is a grid column
   * @param column
   * @returns {boolean}
   */
  function isColumn(column) {
    return column instanceof GridColumn;
  }

  /**
   * Check if given object is a group column
   * @param column
   * @returns {boolean}
   */
  function isGroupColumn(column) {
    return column instanceof GridColumn && !_.isUndefined(column.groupId);
  }

  /**
   * Checks if given object is a grid columns group
   * @param column
   * @returns {boolean}
   */
  function isGroup(column) {
    return column instanceof GridColumnGroup;
  }

  /**
   * Checks if given object is a grid columns group with data driven columns inside
   * @param column
   * @returns {boolean}
   */
  function isDataDrivenGroup(column) {
    return column instanceof GridColumnGroup && !!column.dataDrivenColumn;
  }

  /**
   * Checks if given operand is resolvable
   * @param operand
   * @param fields - array of fields from user settings
   * @returns {boolean}
   */
  function isUnresolvableDependency(operand, fields) {
    return _.isString(operand) && fields.indexOf(operand) === -1;
  }

  /**
   * Create flatten map settings to id -> value array
   * @param settings
   * @returns {Object}
   */
  function flattenMap(settings) {
    return _.reduce(settings, function (map, column) {
      if (isGroup(column)) {
        map[column.id] = column;
        _.reduce(column.columns, function (groupMap, innerColumn) {
          groupMap[innerColumn.id] = innerColumn;
          return groupMap;
        }, map);
      } else {
        map[column.id] = column;
      }
      return map;
    }, {});
  }

  /**
   * Merges default and user settings and pushes to columns array in given group
   * @param group
   * @param defaultValue
   * @param userValue
   * @param fields - array of available field names in user settings
   */
  function applyMergedTo(group, defaultValue, userValue, fields) {
    var userValueJSON = userValue ? userValue.toMergeFormat() : {};
    var defaultValueJSON = defaultValue ? defaultValue.toMergeFormat() : {};
    delete userValueJSON.columns;

    /**
     * For data driven columns we clone default settings,
     * then columns will be added dynamically in GridPanelController
     */
    if (isGroup(userValue) && isDataDrivenGroup(userValue)) {
      var isColumnVisible = userValueJSON.visible;
      userValueJSON = _.cloneDeep(defaultValueJSON);
      userValueJSON.visible = isColumnVisible;
    } else {
      delete defaultValueJSON.columns;
    }
    if (isColumn(userValue)) {
      /**
       * retain original columns group name
       */
      if (isGroup(group)) {
        userValueJSON.groupId = group.groupId;
        userValueJSON.groupTitle = group.title;
      }
      /**
       * retain original title
       */
      if (userValueJSON.title !== defaultValueJSON.title) {
        userValueJSON.originalTitle = defaultValueJSON.title;
      }
      /**
       * hide calculated columns with unresolvable dependencies
       */
      if (!_.isUndefined(userValueJSON.formula) && (isUnresolvableDependency(userValueJSON.formula.leftOperand, fields) || isUnresolvableDependency(userValueJSON.formula.rightOperand, fields))) {
        userValueJSON.visible = false;
        delete userValueJSON.formula;
      }

      /**
       * Check if authorized
       */
      if (!_.isUndefined(defaultValueJSON.authorized) && !defaultValueJSON.authorized) {
        delete userValueJSON.editable;
      }

      /**
       * Check if authorized
       */
      if (!_.isUndefined(defaultValueJSON.editableIf) && !defaultValueJSON.editableIf) {
        delete userValueJSON.editableIf;
      }

      /**
       * Always Prefer Default Value over User Columns
       */
      delete userValueJSON.isUserColumn;
      delete userValueJSON.delayOneClickSelection;
      delete userValueJSON.headerIcon;
      delete userValueJSON.headerIconTooltip;
    }
    if (_.isEmpty(group.columns)) {
      group.columns = [];
    }
    group.columns.push(_.merge({}, defaultValueJSON, userValueJSON));
  }

  /**
   * Create flattened array of columns with their original order
   * Opposite to restoredColumns
   * @param columns
   * @returns {*}
   */
  function flattenColumns(columns) {
    columns = _.clone(columns);
    return _.reduce(columns, function (array, item, index) {
      item.level = 0;
      item.index = index;
      if (isGroup(item)) {
        array.push(item);
        _.reduce(item.columns, function (innerArray, innerColumn, innerIndex) {
          innerColumn.level = 1;
          innerColumn.index = innerIndex;
          innerArray.push(innerColumn);
          return innerArray;
        }, array);
        delete item.columns;
      } else {
        array.push(item);
      }
      return array;
    }, []);
  }

  /**
   * Restore flattened array of columns to standard grid columns setting
   * Opposite to flattenColumns
   * @param columns
   * @returns {*}
   */
  function restoredColumns(columns) {
    return _.chain(columns).cloneDeep().reduce(function (array, item) {
      // building phase
      var group = _.find(array, {
        id: item.groupId
      });
      if (isGroup(item)) {
        item.columns = [];
        array.push(item);
      } else if (isGroupColumn(item) && group) {
        group.columns.push(item);
      } else {
        array.push(item);
      }
      return array;
    }, []).map(function (item) {
      // sorting phase
      if (isGroup(item)) {
        item.columns = _.orderBy(item.columns, 'index', 'asc');
      }
      return item;
    }).orderBy('index', 'asc').value();
  }

  /**
   * Normalize default and user settings
   * @param defaultColumns
   * @param userColumns
   * @returns {{defaultColumns: *, userColumns: *}}
   */
  function normalize(defaultColumns, userColumns) {
    defaultColumns = _.clone(defaultColumns);
    userColumns = _.clone(userColumns);
    var flattenedDefault = flattenColumns(defaultColumns);
    var flattenedUser = flattenColumns(userColumns);
    var compare = function compare(arrVal, othVal) {
      var filedToCompare = ['id', 'visible', 'title', 'level', 'frozen', 'index'];
      return _.isEqual(_.pick(arrVal, filedToCompare), _.pick(othVal, filedToCompare));
    };
    var mergedSettings = _.chain(flattenedDefault).concat(flattenedUser).uniqWith(compare).value();

    //settings which same for both, but modified by user
    var modifiedUserSettings = _.xorWith(mergedSettings, flattenedDefault, compare);

    // separate custom and not custom changes
    var modifiedUserSettingsWithoutCustom = _.filter(modifiedUserSettings, {
      custom: false
    });
    var modifiedUserSettingsOnlyCustom = _.filter(modifiedUserSettings, {
      custom: true
    });

    // add to intersection of default settings all custom settings
    var intersectedDefault = _.chain(flattenedDefault).intersectionWith(mergedSettings, compare).concat(modifiedUserSettingsOnlyCustom).value();

    // go through default settings and replace it with matched modified user settings
    var intersectedUser = intersectedDefault.map(function (item) {
      var modified = _.find(modifiedUserSettingsWithoutCustom, {
        id: item.id
      });
      return modified ? modified : item;
    });
    return {
      defaultColumns: restoredColumns(intersectedDefault),
      userColumns: restoredColumns(intersectedUser)
    };
  }

  /**
   * Merges user grid settings with defaults
   * @param defaultSettings
   * @param userSettings
   * @returns {GridSettingsFactory}
   */
  function mergeGridSettings(defaultSettings, userSettings) {
    if (defaultSettings.gridId !== userSettings.gridId) {
      $log.warn('Grid ID mismatch while merging grid settings.', 'Default grid ID:', defaultSettings.gridId, 'User Grid ID:', userSettings.gridId);
      return defaultSettings;
    }
    if (_.isEmpty(userSettings.columns)) {
      return defaultSettings;
    } else {
      var normalizedSettings = normalize(defaultSettings.columns, userSettings.columns);
      defaultSettings.columns = normalizedSettings.defaultColumns;
      userSettings.columns = normalizedSettings.userColumns;
      var flattenedDefaults = flattenMap(defaultSettings.columns);
      var fields = _.chain(flattenedDefaults).map('field').compact().value();
      var mergedSettings = {
        gridId: userSettings.gridId,
        sortCriteria: userSettings.toMergeFormat().sortCriteria
      };
      if (!_.isUndefined(defaultSettings.scalable)) {
        mergedSettings.scalable = defaultSettings.scalable;
      }
      _.transform(userSettings.columns, function (settings, value) {
        applyMergedTo(settings, flattenedDefaults[value.id], value, fields);
        if (isGroup(value) && !isDataDrivenGroup(value)) {
          _.transform(value.columns, function (groupSettings, innerValue) {
            applyMergedTo(groupSettings.columns[settings.columns.length - 1], flattenedDefaults[innerValue.id], innerValue, fields);
            return groupSettings;
          }, settings);
        }
        return settings;
      }, mergedSettings);
      mergedSettings.dynamicColumns = defaultSettings.dynamicColumns;
      mergedSettings.isDynamic = defaultSettings.isDynamic;
      if (mergedSettings.isDynamic) {
        mergedSettings.dynamicFields = defaultSettings.dynamicFields;
      }
      return new GridSettings(mergedSettings);
    }
  }
  return {
    mergeGridSettings: mergeGridSettings
  };
};
GridSettingsMergeService.$inject = ['$log', 'GridSettingsFactory', 'GridColumnFactory', 'GridColumnGroupFactory'];
module.exports = GridSettingsMergeService;